import { useState, useEffect, useCallback } from "react";
import { Search, ExternalLink, RefreshCw, AlertTriangle } from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY, fmt, pct, Th, usePins, PinButton, PinnedFilterButton } from "./shared";

function _extractDomain(url) {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
  }
}

function MismatchBanner({ meta }) {
  if (!meta) return null;
  const { pages_matched, articles_total, gsc_property_url, wp_url } = meta;
  // Only show when WP is synced, GSC has pages, but 0 matched
  if (!articles_total || pages_matched > 0) return null;

  const gscDomain = gsc_property_url.startsWith("sc-domain:")
    ? gsc_property_url.replace("sc-domain:", "")
    : _extractDomain(gsc_property_url);
  const wpDomain = _extractDomain(wp_url);

  if (!gscDomain || !wpDomain) return null;

  const mismatch = !gscDomain.includes(wpDomain) && !wpDomain.includes(gscDomain);
  if (!mismatch) return null;

  return (
    <div className="flex items-start gap-3 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl text-sm">
      <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-bold text-amber-800">GSC property doesn't match your site URL</p>
        <p className="text-amber-700 mt-0.5 text-xs">
          GSC property: <code className="font-mono">{gsc_property_url}</code>
          {" · "}
          Site: <code className="font-mono">{wp_url}</code>
          {" · "}
          0 of {articles_total} articles matched. Update your GSC property in{" "}
          <a href="/dashboard/settings/search-console" className="underline font-bold">Settings</a>.
        </p>
      </div>
    </div>
  );
}

export default function PagesTab({ onDaysBackDetected }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("clicks");
  const [order, setOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const pins = usePins("page");
  const LIMIT = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, order, limit: LIMIT, offset };
      if (debouncedSearch) params.search = debouncedSearch;
      if (pinnedOnly) params.pinned = true;
      const r = await api.getPerformancePages(params);
      setRows(r.pages || []);
      setTotal(r.total || 0);
      setMeta(r.meta || null);
      if (r.meta?.days_back) onDaysBackDetected?.(r.meta.days_back);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sort, order, debouncedSearch, pinnedOnly, offset]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sort === col) {
      setOrder(o => o === "desc" ? "asc" : "desc");
    } else {
      setSort(col);
      setOrder("desc");
    }
    setOffset(0);
  };

  return (
    <div>
      <MismatchBanner meta={meta} />
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Search pages..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-zinc-100 bg-white rounded-lg focus:outline-none focus:ring-2 placeholder-zinc-400"
            style={{ "--tw-ring-color": PRIMARY }}
          />
        </div>
        <PinnedFilterButton
          active={pinnedOnly}
          count={pins.count}
          onClick={() => { setPinnedOnly(v => !v); setOffset(0); }}
        />
        <span className="text-xs text-zinc-400">{total} pages</span>
        <button onClick={load} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="border-b border-zinc-100 bg-zinc-50">
              <tr>
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400 min-w-[260px]">Page</th>
                <Th col="clicks"      label="Clicks"      sort={sort} order={order} onSort={handleSort} />
                <Th col="impressions" label="Impressions" sort={sort} order={order} onSort={handleSort} />
                <Th col="ctr"         label="CTR"         sort={sort} order={order} onSort={handleSort} />
                <Th col="position"    label="Position"    sort={sort} order={order} onSort={handleSort} />
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400">Top Query</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">
                  No page data yet. Run GSC Refresh from Settings to populate.
                </td></tr>
              )}
              {!loading && rows.map((row, i) => {
                const topQ = Array.isArray(row.top_queries) ? row.top_queries[0] : null;
                const pageUrl = row.page || row.wp_url || "";
                const displayPath = pageUrl.replace(/^https?:\/\/[^/]+/, "") || "/";
                return (
                  <tr key={row.wp_post_id || pageUrl || i} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="px-3 py-2 min-w-[260px] max-w-[420px]">
                      <div className="flex items-start gap-1">
                        {pageUrl && <PinButton pinned={pins.isPinned(pageUrl)} onClick={() => pins.toggle(pageUrl)} />}
                        <div className="min-w-0">
                          <p className="font-medium text-zinc-900 truncate" title={row.title || displayPath}>
                            {row.title || displayPath}
                          </p>
                          {pageUrl && (
                            <a
                              href={pageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-zinc-600 mt-0.5 truncate"
                            >
                              <ExternalLink size={9} />
                              {pageUrl.replace(/^https?:\/\//, "").slice(0, 60)}
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-bold text-zinc-900 tabular-nums">
                      {fmt(row.clicks ?? row.gsc_clicks_28d)}
                    </td>
                    <td className="px-3 py-2 text-zinc-700 tabular-nums">
                      {fmt(row.impressions ?? row.gsc_impressions_28d)}
                    </td>
                    <td className="px-3 py-2 text-zinc-700 tabular-nums">
                      {pct(row.ctr ?? row.gsc_ctr)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {(() => {
                        const pos = row.position ?? row.gsc_position;
                        return (
                          <span className={`font-bold ${
                            pos <= 3 ? "text-green-600"
                            : pos <= 10 ? "text-amber-600"
                            : "text-red-500"
                          }`}>
                            {fmt(pos, 1)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-500 max-w-[160px] truncate">
                      {topQ ? topQ.query : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
          <span>Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
              className="px-3 py-1.5 border border-zinc-100 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >Previous</button>
            <button
              disabled={offset + LIMIT >= total}
              onClick={() => setOffset(o => o + LIMIT)}
              className="px-3 py-1.5 border border-zinc-100 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
